import type { Palette } from '@mui/material'
import { alpha } from '@mui/material'

export const black = '#000000'
export const white = '#FFFFFF'

type MakePaletteProps = {
  palette: Partial<Palette>
  action: {
    color: string
    opacities: {
      active: number
      hover: number
      selected: number
      disabled: number
      focus: number
    }
  }
}

export default function makePalette({ action, palette }: MakePaletteProps) {
  return {
    ...palette,

    primary: {
      main: '#3E77FE',
      dark: '#2B53B1',
      light: '#6492FE',
      contrastText: white
    },

    secondary: {
      main: '#222E65',
      dark: '#172046',
      light: '#4E5783',
      contrastText: white
    },

    action: {
      activatedOpacity: action.opacities.active,
      active: alpha(action.color, action.opacities.active),
      hoverOpacity: action.opacities.hover,
      hover: alpha(action.color, action.opacities.hover),
      selectedOpacity: action.opacities.selected,
      selected: alpha(action.color, action.opacities.selected),
      disabledOpacity: action.opacities.disabled,
      disabled: alpha(action.color, action.opacities.disabled),
      disabledBackground: alpha(action.color, 0.12),
      focusOpacity: action.opacities.focus,
      focus: alpha(action.color, action.opacities.focus)
    },

    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#F5F5F5',
      A200: '#EEEEEE',
      A400: '#BDBDBD',
      A700: '#616161'
    },

    info: {
      main: '#00A0FF',
      dark: '#0070B2',
      light: '#33B3FF',
      contrastText: white
    },

    success: {
      main: '#03AC1E',
      dark: '#027815',
      light: '#B3E6BB',
      contrastText: white
    },

    warning: {
      main: '#FFA600',
      dark: '#b27400',
      light: '#FFB733',
      contrastText: white
    },

    error: {
      main: '#D32F2F',
      dark: '#B13C25',
      light: '#EF5350',
      contrastText: white
    },

    common: {
      black,
      white
    },

    purple: {
      main: '#9C27B0',
      light: '#BA68C8',
      dark: '#7B1FA2',
      contrastText: white
    },
    orange: {
      main: '#EF6C00',
      light: '#FF9800',
      dark: '#D05E00',
      contrastText: white
    },
    teal: {
      main: '#00787F',
      light: '#00B3BE',
      dark: '#006268',
      contrastText: white
    },
    aqua: {
      main: '#5EACCC',
      light: '#8EC5DB',
      dark: '#388EB1',
      contrastText: white
    },
    cornflower: {
      main: '#6689E7',
      light: '#7E9CEB',
      dark: '#3463DF',
      contrastText: white
    },
    navy: {
      main: '#4A529E',
      light: '#5F67B4',
      dark: '#3D4381',
      contrastText: white
    },
    sky: {
      main: '#A4D5F2',
      light: '#CEEDFF',
      dark: '#78BEE7',
      contrastText: alpha(black, 0.87)
    },
    unknown: {
      main: '#E0E0E0',
      light: '#E0E0E0',
      dark: '#E0E0E0',
      contrastText: black
    },
    lime: {
      main: '#8CC119',
      light: '#CAEA84',
      dark: '#629000',
      contrastText: white
    }
  } satisfies Partial<Palette>
}
